import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { BodyText, Button, Icon, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderShareLinkRow = (props: Props) => {
  const { joinLink, onCopyLink } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [hasCopied, setHasCopied] = useState(false);

  // ─── Animation ───────────────────────────────────────────────────────

  const copyProgressSV = useDerivedValue(() => withTiming(hasCopied ? 0 : 1));
  const ctaLabelContainerStyles = useAnimatedStyle(() => ({ opacity: copyProgressSV.value }));

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleCopyLink = useCallback(async () => {
    const hasCopied = await onCopyLink();

    if (!hasCopied) return;

    setHasCopied(true);

    setTimeout(() => {
      setHasCopied(false);
    }, 800);
  }, [onCopyLink]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <View style={styles.linkPill}>
        <BodyText sizeMatch={['14']} numberOfLines={1}>
          {joinLink}
        </BodyText>
      </View>

      <Button palette="primary" size="large" onPress={handleCopyLink}>
        <View>
          <Animated.View style={ctaLabelContainerStyles}>
            <BodyText sizeMatch={['16']} style={styles.copyLinkLabel}>
              <FormattedMessage {...messages.copyLinkLabel} />
            </BodyText>
          </Animated.View>

          {hasCopied ? (
            <Animated.View
              pointerEvents="none"
              entering={FadeIn}
              exiting={FadeOut}
              style={styles.copySuccessContainer}
            >
              <Icon name="IconCheck" width={18} height={18} color={theme.colors.CREAM} />
            </Animated.View>
          ) : null}
        </View>
      </Button>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  copyLinkLabel: {
    defaultMessage: 'Copy link',
    description: 'Group Order Share | Modal | Copy link CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['3'],
    gap: theme.spacing['2'],
  },
  linkPill: {
    flex: 1,
    minHeight: 48,
    justifyContent: 'center',
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['5'],
  },
  copySuccessContainer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.xlarge,
  },
  copyLinkLabel: {
    color: theme.colors.CREAM,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  joinLink: string;
  onCopyLink: () => boolean | Promise<boolean>;
};
